import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Stack, Table, TableBody, TableHead, TableRow, Typography, TableCell, TableContainer, Menu, MenuItem, Divider, ListItem, Box, CircularProgress } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Security } from '../../proto/generated/security'
import websocketActions from '../../actions/websocket.actions'
import { Watchlist_WatchlistItem_Type } from '../../proto/generated/watchlist'
import EmptyWatchlist from '../../assets/icons/EmptyWatchlist'
import { removewatchlistItem, setPreviewWatchlist as setPreviewWatchlistAction, setWatchlists, Watchlist, WatchlistItemData } from '../../reducer/watchList.reducer'
import { selectCommunityWatchlists, selectPreviewWatchlist, selectPreviewWatchlistId, selectWatchlists } from '../../selectors/watchList'
import WatchlistTableView, { WatchListDataForSort } from './WatchlistTableView'
import { makeStyles } from 'tss-react/mui'
import WatchListNormalView from './WatchListNormalView'
import { selectCustomizedColumns, selectNumberOfCustomizedWatchlistColumns, watchListView } from '../../selectors/user'
import { NonInteractiveTooltip } from '../custom'
import ThinArrowDownwardIcon from '../../assets/icons/ThinArrowDownwardIcon'
import ThinArrowUpwardIcon from '../../assets/icons/ThinArrowUpwardIcon'
import { calculateSnapshotDetails, getExchangeAndCodeFromKey, getSecurityAsync, getSecurityKey } from '../../util/security'
import { calculateSnapshotDetailsForExpression } from '../../util/expression'
import { selectSnapshots } from '../../selectors/security'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { selectPrimarySecurity } from '../../selectors/home'
import useCheckAnyElementFocused from '../../hooks/useCheckAnyElementFocused'
import { selectCommonStore } from '../../selectors/common'
import { WidgetListItems } from '../../util/sidebar'
import WatchListGroupedPanel from './WatchListGroupedPanel'
import { changeWatchlistSecurities, getSharedWatchlist } from '../../services/apis/watchlist'
import {updateWatchlistInState } from './WatchUtils'
import { primary as primaryColor } from '../../assets/colors'
import { useShouldAllowAccess } from '../../hooks/useWatchlistAccess'
import { useAdminScreenerAccess } from '../../hooks/useAdminScreenerAccess'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles.ts'
interface WatchListDataProps {
    addWatchListSection?: Function
    addOrRemoveWatchlistItem: Function
    setModalOpen: Function
    onDrop: Function
    focusedIndex: number | null
    setFocusedIndex: React.Dispatch<React.SetStateAction<number | null>>
    editMode: boolean
}
const useStyles = makeStyles()((theme) => ({
    stackRoot: {
        flex: 1,
        overflowY: 'auto',
        height: '100%'
    },
    emptyWatchlistContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        height: '100%',
        width: '100%',
        gap: theme.spacing(2.5),
        paddingX: theme.spacing(2),
        color: theme.palette.text.secondary
    }
}))

export default function WatchListData(props: WatchListDataProps) {
    const dispatch = useDispatch()
    const { focusedIndex, setFocusedIndex } = props
    const { primary, key: primaryKey, expressionMode } = useSelector(selectPrimarySecurity)
    const isAnyElementFocused = useCheckAnyElementFocused()
    const watchlists = useSelector(selectWatchlists)
    const [securitiesToSubscribe, setSecuritiesToSubscribe] = useState<Security[]>([])
    const [draggedItem, setDraggedItem] = useState<WatchlistItemData | null>(null)
    const [draggedOverItemIndex, setDraggedOverItemIndex] = useState<number | null>(null)
    const { classes } = useStyles()
    const [selectedItems, setSelectedItems] = useState<WatchlistItemData[]>([]);
    const tableView = useSelector(watchListView);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const snapshots = useSelector(selectSnapshots)
    const customsizedColumnsTable = useSelector(selectCustomizedColumns)
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const { rightSideWidget } = useSelector(selectCommonStore)
    const isWatchlistOpen = rightSideWidget === WidgetListItems.WATCHLIST;
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [focusTriggeredByKeyboard, setFocusTriggeredByKeyboard] = useState(false);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => { setAnchorEl(event.currentTarget); };
    const numberOfTableColumns = useSelector(selectNumberOfCustomizedWatchlistColumns)
    const shouldAllowAccess = useShouldAllowAccess();
    const shouldAllowAdminAccess = useAdminScreenerAccess();
    const communityWatchlists = useSelector(selectCommunityWatchlists);
    const previewId = useSelector(selectPreviewWatchlistId);
    const [previewWatchlist, setPreviewWatchlist] = useState<Watchlist | null>(null);
    const previousPreviewWatchlistIdRef = useRef<string | undefined>(undefined)
    const selectedWatchlistIdFromStore = useSelector(
        (state: RootState) => state.commonStore.watchlistId
    );
    const selectedWatchlistId = useMemo(() => {
        if (previewId.isActive && previewId.watchlistId) {
            return previewId.watchlistId;
        }
        return selectedWatchlistIdFromStore;
    }, [previewId, selectedWatchlistIdFromStore]);
    const selectedWatchlist = useMemo(() => {
        if (previewId.isActive && previewWatchlist) {
            return previewWatchlist
        }
        return watchlists[selectedWatchlistId]
    }, [previewId.isActive, previewWatchlist, watchlists, selectedWatchlistId])
    const watchlistRef = useRef<{initialLoaded: boolean;prevSrcWatchlistId?: string;}>({
        initialLoaded: false,
        prevSrcWatchlistId: undefined,
    });
    const SORT_OPTIONS = [
        { label: 'Change %', column: 'changePercentage', direction: 'asc' },
        { label: 'Change %', column: 'changePercentage', direction: 'desc' },
        { label: 'Change', column: 'change', direction: 'asc' },
        { label: 'Change', column: 'change', direction: 'desc' },
        { label: 'Last', column: 'last', direction: 'asc' },
        { label: 'Last', column: 'last', direction: 'desc' },
        { label: 'Symbol', column: 'symbol', direction: 'asc' },
        { label: 'Symbol', column: 'symbol', direction: 'desc' },
    ];

    const handleSort = (columnKey: string) => {
        if (sortColumn === columnKey) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(columnKey);
            setSortDirection('asc');
        }
    };

    const [allWatchListDataForSorting, setAllWatchListDataForSorting] = useState<(WatchlistItemData & { stats: WatchListDataForSort })[]>([])

    useEffect(() => {
        if (!selectedWatchlist?.watchlistItems?.length) {
            setAllWatchListDataForSorting([])
            return
        }
        let cancelled = false
        const run = async () => {
            const items = selectedWatchlist.watchlistItems
            const result = await Promise.all(items.map(async (item) => {
                let stats: WatchListDataForSort = { ltp: 0, ltpChange: 0, changePercentage: 0, volume: 0 };
                if (item.item.type === Watchlist_WatchlistItem_Type.SECURITY && item.item.security) {
                    const key = getSecurityKey(item.item.security.exchange, item.item.security.code);
                    const security = await getSecurityAsync(key);
                    stats = calculateSnapshotDetails(security, snapshots[key]) || stats;
                } else if (item.item.type === Watchlist_WatchlistItem_Type.EXPRESSION && item.item.securityExpression) {
                    const rawExpression = item.item.securityExpression.expression;
                    stats = calculateSnapshotDetailsForExpression(rawExpression, item.item.securityExpression.securities, snapshots) || stats;
                }
                return { ...item, stats };
            }))
            if (!cancelled) setAllWatchListDataForSorting(result)
        }
        run()
        return () => { cancelled = true }
    }, [selectedWatchlist?.watchlistItems, snapshots])

    const sortedWatchlistItems = useMemo(() => {
        if (!sortColumn) return allWatchListDataForSorting;

        const sortFn = (a: any, b: any) => {
            let aValue: number | string = 0;
            let bValue: number | string = 0;

            switch (sortColumn) {
                case 'symbol':
                    aValue = a.item.security?.symbol || '';
                    bValue = b.item.security?.symbol || '';
                    break;
                case 'last':
                    aValue = a.stats.ltp;
                    bValue = b.stats.ltp;
                    break;
                case 'change':
                    aValue = a.stats.ltpChange;
                    bValue = b.stats.ltpChange;
                    break;
                case 'changePercentage':
                    aValue = a.stats.changePercentage;
                    bValue = b.stats.changePercentage;
                    break;
                case 'volume':
                    aValue = a.stats.volume;
                    bValue = b.stats.volume;
                    break;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return sortDirection === 'asc'
                ? (aValue as number) - (bValue as number)
                : (bValue as number) - (aValue as number);
        };

        const result: any[] = [];
        const ungroupedItems: any[] = [];

        let currentGroup: any[] = [];

        const finalizeCurrentSectionGroup = () => {
            if (currentGroup.length === 0) return;

            const [sectionItem, ...groupItems] = currentGroup;

            const sectionName = sectionItem.item.sectionName?.toLowerCase() || '';
            const isDefaultSection = sectionName.includes('default');
            const validChildren = groupItems.filter(i => i.item.type !== Watchlist_WatchlistItem_Type.SECTION);

            if (!isDefaultSection || validChildren.length > 0) {
                result.push(sectionItem);
                result.push(...validChildren.sort(sortFn));
            }

            currentGroup = [];
        };

        for (const item of allWatchListDataForSorting) {
            if (item.item.type === Watchlist_WatchlistItem_Type.SECTION) {
                finalizeCurrentSectionGroup();
                currentGroup.push(item);
            } else if (currentGroup.length > 0) {
                currentGroup.push(item);
            } else {
                ungroupedItems.push(item);
            }
        }

        finalizeCurrentSectionGroup();
        const sortedUngrouped = ungroupedItems.sort(sortFn);
        return [...sortedUngrouped, ...result];
    }, [allWatchListDataForSorting, sortColumn, sortDirection]);

    const flatFocusableItems = useMemo(() => {
        return sortedWatchlistItems.filter(
            (item) => item.item.type === Watchlist_WatchlistItem_Type.SECURITY || item.item.type === Watchlist_WatchlistItem_Type.EXPRESSION
        );
    }, [sortedWatchlistItems]);


    useEffect(() => {
        if (!isWatchlistOpen) return;
        if ((primary || primaryKey) && flatFocusableItems.length) {
            const index = flatFocusableItems.findIndex((item) => {
                if (item.item.type === Watchlist_WatchlistItem_Type.SECURITY && item.item.security) {
                    const sec = item.item.security;
                    // Match by exchange + code when primary Security exists (fixes multi-exchange bug)
                    if (primary) {
                        return sec.exchange === primary.exchange && sec.code === primary.code;
                    }
                    // Fallback: match by key when we only have key (e.g. skeleton before resolution)
                    if (primaryKey) {
                        return getSecurityKey(sec.exchange, sec.code) === primaryKey;
                    }
                    return false;
                }
                if (item.item.type === Watchlist_WatchlistItem_Type.EXPRESSION && expressionMode && primaryKey) {
                    return item.item.securityExpression?.expression === primaryKey;
                }
                return false;
            });
            setFocusedIndex(index !== -1 ? index : null);
        }
    }, [isWatchlistOpen, primary, primaryKey, expressionMode, flatFocusableItems, setFocusedIndex]);

    useEffect(() => {
        if (!selectedWatchlist?.shareId || communityWatchlists.length === 0) {
            return;
        }
        const srcWatchlist = communityWatchlists.find(
            (w) => w.shareId === selectedWatchlist.shareId
        );
        if (!srcWatchlist || !srcWatchlist.screenerId || srcWatchlist.screenerId.trim() === "") {
            return;
        }
        let interval: ReturnType<typeof setInterval> | undefined;
        const fetchFirstTime = async (isInitial = false) => {    
            if(!previewId.isActive){
                await updateWatchlistInState(selectedWatchlist.id , dispatch);
            }  
        };

        const shouldTriggerInitial =
            !watchlistRef.current.initialLoaded ||
            watchlistRef.current.prevSrcWatchlistId !== srcWatchlist.id;

        if (shouldTriggerInitial) {
            watchlistRef.current.initialLoaded = true;
            watchlistRef.current.prevSrcWatchlistId = srcWatchlist.id;
        }

        interval = setInterval(fetchFirstTime, 30000);
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [selectedWatchlist?.id, selectedWatchlist?.shareId, communityWatchlists]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isAnyElementFocused) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    setFocusTriggeredByKeyboard(true);
                }

                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setFocusedIndex((prev: any) =>
                        prev < flatFocusableItems.length - 1 ? prev + 1 : 0
                    );
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setFocusedIndex((prev: any) =>
                        prev > 0 ? prev - 1 : flatFocusableItems.length - 1
                    );
                }
            }
        };

        const handleMouseDown = () => {
            setFocusTriggeredByKeyboard(false);
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('mousedown', handleMouseDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('mousedown', handleMouseDown);
        };
    }, [isAnyElementFocused, flatFocusableItems.length, setFocusedIndex]);

    const lastFetchedPreviewIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (previewId.isActive && previewId.shareId) {
            if (lastFetchedPreviewIdRef.current === previewId.shareId) {
                // Already fetched this preview → skip
                return;
            }

            const fetchPreviewWatchlist = async () => {
                try {
                    const response = await getSharedWatchlist({ shareId: previewId.shareId! });

                    const processedItems = response.watchlistItems
                        .map((watchlistItem) => {
                            let itemId = '';
                            const { type, security, securityExpression, sectionName } = watchlistItem;

                            if (type === Watchlist_WatchlistItem_Type.SECURITY && security) {
                                itemId = getSecurityKey(security.exchange, security.code);
                            } else if (type === Watchlist_WatchlistItem_Type.EXPRESSION && securityExpression) {
                                itemId = securityExpression.expression;
                            } else if (type === Watchlist_WatchlistItem_Type.SECTION && sectionName !== undefined) {
                                itemId = sectionName;
                            }

                            return { id: itemId, item: watchlistItem };
                        })
                        .filter(({ id, item }) => {
                            const { type, security, securityExpression, sectionName } = item;
                            return (
                                id &&
                                ((type === Watchlist_WatchlistItem_Type.SECURITY && security) ||
                                    (type === Watchlist_WatchlistItem_Type.EXPRESSION &&
                                        securityExpression &&
                                        !securityExpression.expired &&
                                        securityExpression.securities.length > 0) ||
                                    (type === Watchlist_WatchlistItem_Type.SECTION && sectionName !== undefined))
                            );
                        });

                    const processedWatchlist: Watchlist = {
                        ...response,
                        watchlistItems: processedItems,
                        type: Array.isArray(response.type) ? response.type : [response.type],
                    };

                    setPreviewWatchlist(processedWatchlist);
                    dispatch(setPreviewWatchlistAction(processedWatchlist));

                    lastFetchedPreviewIdRef.current = previewId.shareId;
                } catch (error) {
                    console.error('Error fetching preview watchlist:', error);
                    setPreviewWatchlist(null);
                }
            };

            fetchPreviewWatchlist();
        } else {
            // Cleanup when preview closes
            setPreviewWatchlist(null);
            lastFetchedPreviewIdRef.current = null;
        }
    }, [previewId, dispatch]);

    const handleItemClick = useCallback(
        (e: React.MouseEvent, item: WatchlistItemData) => {
            setSelectedItems(prev => {
                const exists = prev.some(i => i.id === item.id);
                let newSelection: WatchlistItemData[];

                if (e.metaKey || e.ctrlKey) {

                    newSelection = exists
                        ? prev.filter(i => i.id !== item.id)
                        : [...prev, item];
                } else {
                    newSelection = [item];
                }

                return newSelection;

            });
            const index = flatFocusableItems.findIndex(f => f.id === item.id);
            if (index !== -1) {
                setFocusedIndex(index);
            }
        },
        [flatFocusableItems, setFocusedIndex]
    );
    useEffect(() => {
        if(previewId && previewId.isActive){
            const securityKeys: Set<string> = new Set()
            // Use selectedWatchlist which handles both preview and regular watchlists
            if (selectedWatchlist) {
                selectedWatchlist.watchlistItems.forEach((wlItem) => {
                    if (wlItem?.item?.type === Watchlist_WatchlistItem_Type.SECURITY && wlItem.item.security) {
                        securityKeys.add(getSecurityKey(wlItem.item.security.exchange, wlItem.item.security.code))
                    } else if (wlItem?.item?.type === Watchlist_WatchlistItem_Type.EXPRESSION && wlItem?.item?.securityExpression) {
                        wlItem.item.securityExpression!.securities.forEach((security: Security) => {
                            securityKeys.add(getSecurityKey(security.exchange, security.code))
                        })
                    }
                })
            }

            // Subscribe to security snapshots using the subscription tag
            if (securityKeys.size > 0) {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: [
                        {
                            tag: 'watchListSecuritySnapshots',
                            securities: Array.from(securityKeys).map((securityKey) => {
                                const [exc, code] = getExchangeAndCodeFromKey(securityKey)
                                return {
                                    exchange: exc,
                                    code: code
                                }
                            })
                        }
                    ]
                })

                // Fetch initial snapshots
                fetchSecuritySnapshotsBulk({
                    securities: Array.from(securityKeys)
                        .map((securityKey: string) => {
                            const [exc, code] = getExchangeAndCodeFromKey(securityKey)
                            return {
                                exchange: exc,
                                code: code
                            }
                        })
                        .filter((sec) => sec !== undefined),
                    dispatch: dispatch
                }).catch((error) => {
                    console.error('Error fetching security data', error)
                })
            } else {
                // Clear subscriptions when no securities
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: []
                })
            }
        }
    }, [selectedWatchlist, dispatch]) 

    const subscribeOrderBook = useCallback((security: Security, subscribe: boolean) => {
        if (subscribe) {
            setSecuritiesToSubscribe([...securitiesToSubscribe, security])
        } else {
            const securityToRemove = securitiesToSubscribe.find(
                (listSecurity) => listSecurity.code === security.code
            )
            if (securityToRemove) {
                setSecuritiesToSubscribe(securitiesToSubscribe.filter(
                    (listSecurity) => listSecurity.code !== securityToRemove.code
                ))
            }
        }
    }, [securitiesToSubscribe])

    const handleSectionDelete = useCallback((
        watchlistItems: WatchlistItemData[],
        add: boolean,
        watchlistId: string
    ) => {
        if (!add && watchlistItems.length > 0) {
            const itemsToDelete = watchlistItems.map(w => w.item);
            changeWatchlistSecurities({
                watchlistItem: { watchlistItems: itemsToDelete },
                watchlistId,
                method: 'DELETE'
            }).then(() => {
                dispatch(removewatchlistItem({
                    itemId: itemsToDelete.map(i=>i.id),
                    watchlistId: watchlistId
                }))
            }).catch((err) => {
                console.error('Error deleting section:', err);
            });
        }
    }, [dispatch]);

    const onDragEnd = useCallback((dragImg: HTMLDivElement) => {
        return function () {
            if (document.body.contains(dragImg)) {
                document.body.removeChild(dragImg)
            }
            document.removeEventListener('dragend', onDragEnd(dragImg))
        }
    }, [])

    const handleDragStart = useCallback((e: React.DragEvent<HTMLLIElement>, watchlistItemData: WatchlistItemData) => {
        const isSelected = selectedItems.find(i => i.id === watchlistItemData.id);
        const itemsToDrag = isSelected ? selectedItems : [watchlistItemData];
        setDraggedItem(watchlistItemData)
        const dragImg = document?.createElement('div')
        dragImg.style.padding = '0 15px'
        dragImg.style.width = '300px'
        dragImg.style.backgroundColor = '#1890ff22'
        dragImg.innerHTML = itemsToDrag
            .map(item => item.item.security?.symbol || item.item.sectionName || `ID: ${item.id}`)
            .join('<br/>');
        document?.body.appendChild(dragImg)
        e.dataTransfer.setDragImage(dragImg, -10, 0)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('application/json', JSON.stringify(itemsToDrag));
        document.addEventListener('dragend', onDragEnd(dragImg))
    }, [onDragEnd, selectedItems])

    const handleDragOver = useCallback(
        (e: React.DragEvent<HTMLLIElement>, index: number | null) => {
            e.preventDefault()
            setDraggedOverItemIndex(index)
        },
        []
    )
    const handleDragEnd = useCallback(
        () => {
            const itemsToDrop = selectedItems.length > 0 ? selectedItems : draggedItem ? [draggedItem] : [];
            if (draggedOverItemIndex !== null && draggedItem?.id !== sortedWatchlistItems[draggedOverItemIndex]?.id && itemsToDrop.length > 0) {
                props.onDrop(itemsToDrop, sortedWatchlistItems[draggedOverItemIndex]);
            }
            setSelectedItems([]);
            setDraggedItem(null)
            setDraggedOverItemIndex(null)
        },
        [selectedItems, draggedItem, draggedOverItemIndex, sortedWatchlistItems, props]
    )
    const onAddSymbolButtonClick = useCallback(() => {
        props.setModalOpen(true)
    }, [props])


    return (
        <Stack className={classes.stackRoot}>
            {tableView && selectedWatchlist?.watchlistItems?.length > 0 ? (
                <TableContainer style={{ overflowX: 'hidden', width: '100%', height: '100%' }}>
                    <Table stickyHeader style={{ borderCollapse: 'separate', fontSize: 13, height: 'auto' }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    height: 32,
                                    '& th': {
                                        paddingY: '4px',
                                        paddingX: '8px',
                                        fontSize: 12,
                                        lineHeight: 1.2
                                    }
                                }}
                            >
                                <TableCell
                                    component="th"
                                    sx={{ borderBottomColor: (theme) => theme.palette.divider }}
                                    style={{ padding: '8px', paddingLeft: '15px', width: numberOfTableColumns === 1 ? '100%' : '120px' }}
                                >
                                    <NonInteractiveTooltip
                                        title={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                Click to sort by symbol
                                                {sortColumn === 'symbol' ? (
                                                    sortDirection === 'asc' ? (
                                                        <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )
                                                ) : (
                                                    <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                )}
                                            </span>
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <div
                                            style={{
                                                fontSize: '13px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                fontWeight: sortColumn === 'symbol' ? 'bold' : 'normal',
                                                width: '100%'
                                            }}
                                            onClick={() => handleSort('symbol')}
                                        >
                                            Symbol
                                            {sortColumn === 'symbol' &&
                                                (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                        </div>
                                    </NonInteractiveTooltip>
                                </TableCell>
                                {customsizedColumnsTable.last ? (
                                    <TableCell
                                        sx={{ borderBottomColor: (theme) => theme.palette.divider }}
                                        style={{
                                            textAlign: 'right'
                                        }}
                                    >
                                        <NonInteractiveTooltip
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Click to sort by last
                                                    {sortColumn === 'last' ? (
                                                        sortDirection === 'asc' ? (
                                                            <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                        ) : (
                                                            <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                        )
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </span>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    fontSize: '13px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: sortColumn === 'last' ? 'bold' : 'normal',
                                                    width: '100%',
                                                    justifyContent: 'flex-end'
                                                }}
                                                onClick={() => handleSort('last')}
                                            >
                                                Last
                                                {sortColumn === 'last' &&
                                                    (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                            </div>
                                        </NonInteractiveTooltip>
                                    </TableCell>
                                ) : null}

                                {customsizedColumnsTable.change && (
                                    <TableCell
                                        component="th"
                                        style={{
                                            textAlign: 'right',
                                            borderBottom: 'none'
                                        }}
                                    >
                                        <NonInteractiveTooltip
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Click to sort by change
                                                    {sortColumn === 'change' ? (
                                                        sortDirection === 'asc' ? (
                                                            <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                        ) : (
                                                            <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                        )
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </span>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: sortColumn === 'change' ? 'bold' : 'normal',
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                    fontSize: '13px'
                                                }}
                                                onClick={() => handleSort('change')}
                                            >
                                                Chg
                                                {sortColumn === 'change' &&
                                                    (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                            </div>
                                        </NonInteractiveTooltip>
                                    </TableCell>
                                )}
                                {customsizedColumnsTable.changePercentage && (
                                    <TableCell
                                        component="th"
                                        style={{
                                            textAlign: 'right',
                                            borderBottom: 'none'
                                        }}
                                    >
                                        <NonInteractiveTooltip
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Click to sort by Chg%
                                                    {sortColumn === 'changePercentage' ? (
                                                        sortDirection === 'asc' ? (
                                                            <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                        ) : (
                                                            <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                        )
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </span>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: sortColumn === 'changePercentage' ? 'bold' : 'normal',
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                    fontSize: '13px'
                                                }}
                                                onClick={() => handleSort('changePercentage')}
                                            >
                                                Chg%
                                                {sortColumn === 'changePercentage' &&
                                                    (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                            </div>
                                        </NonInteractiveTooltip>
                                    </TableCell>
                                )}
                                {customsizedColumnsTable.volume && (
                                    <TableCell
                                        component="th"
                                        style={{
                                            textAlign: 'right',
                                            borderBottom: 'none',
                                            paddingRight: '20px'
                                        }}
                                    >
                                        <NonInteractiveTooltip
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Click to sort by volume
                                                    {sortColumn === 'volume' ? (
                                                        sortDirection === 'asc' ? (
                                                            <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />
                                                        ) : (
                                                            <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                        )
                                                    ) : (
                                                        <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </span>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: sortColumn === 'volume' ? 'bold' : 'normal',
                                                    width: '100%',
                                                    justifyContent: 'flex-end',
                                                    fontSize: '13px'
                                                }}
                                                onClick={() => handleSort('volume')}
                                            >
                                                Vol
                                                {sortColumn === 'volume' &&
                                                    (sortDirection === 'asc' ? <ThinArrowDownwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowUpwardIcon style={{ fontSize: '14px' }} />)}
                                            </div>
                                        </NonInteractiveTooltip>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody
                            sx={{
                                height: '100%'
                            }}
                        >
                            {selectedWatchlist?.watchlistItems?.length > 0 ? (
                                sortedWatchlistItems.some((item) => item.item.type === Watchlist_WatchlistItem_Type.SECTION) ? (
                                    <WatchListGroupedPanel
                                        data={sortedWatchlistItems}
                                        draggedOverItem={draggedOverItemIndex !== null ? sortedWatchlistItems[draggedOverItemIndex] : null}
                                        selectedWatchlistId={selectedWatchlistId}
                                        handleWatchlistItemAddOrDelete={handleSectionDelete}
                                        subscribeOrderBook={subscribeOrderBook}
                                        handleDragOver={handleDragOver}
                                        handleDragEnd={handleDragEnd}
                                        handleDragStart={handleDragStart}
                                        onItemClick={handleItemClick as any}
                                        selectedItemIds={new Set(selectedItems?.map((i) => i?.id))}
                                        totalItems={flatFocusableItems.length}
                                        focusedIndex={focusedIndex}
                                        setFocusedIndex={setFocusedIndex}
                                        isFocused={focusedIndex as any}
                                        focusTriggeredByKeyboard={focusTriggeredByKeyboard}
                                        editMode={props.editMode}
                                        selectedWatchlist={selectedWatchlist}
                                    />
                                ) : (
                                    sortedWatchlistItems.map((data, index) => {
                                        const isDraggedOver = draggedOverItemIndex === index
                                        return (
                                            <WatchlistTableView
                                                key={data.id}
                                                isDraggedOver={draggedOverItemIndex !== null && sortedWatchlistItems[draggedOverItemIndex]?.id === data.id}
                                                data={data}
                                                watchlistId={selectedWatchlistId}
                                                addOrRemoveWatchlistDataItem={handleSectionDelete}
                                                subscribeSecurity={subscribeOrderBook}
                                                handleDragOver={handleDragOver}
                                                handleDragEnd={handleDragEnd}
                                                handleDragStart={handleDragStart}
                                                index={index}
                                                isFocused={focusedIndex === index}
                                                totalItems={sortedWatchlistItems.length}
                                                setFocusedIndex={setFocusedIndex}
                                                onItemClick={handleItemClick as any}
                                                selectedItemIds={new Set(selectedItems?.map((i) => i?.id))}
                                                focusTriggeredByKeyboard={focusTriggeredByKeyboard}
                                                showBottomDragLine={isDraggedOver}
                                                editMode={props.editMode}
                                            />
                                        )
                                    })
                                )
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Stack className={classes.emptyWatchlistContainer}>
                                            <EmptyWatchlist />
                                            <Stack>
                                                <Typography variant="body2">{'Your watchlist is waiting for action!'}</Typography>
                                                <Typography variant="body2">{"Add a symbol and let's get started"}</Typography>
                                            </Stack>
                                            {watchlists && selectedWatchlist && shouldAllowAccess(selectedWatchlist, props.editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && (
                                                <Button variant="contained" color="inherit" onClick={onAddSymbolButtonClick}>
                                                    Add symbol
                                                </Button>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )}
                            {
                                <TableRow sx={{ height: '1px', boxSizing: 'border-box' }}>
                                    {draggedOverItemIndex === sortedWatchlistItems.length ? (
                                        <TableCell colSpan={numberOfTableColumns} padding={'none'} sx={{ maxHeight: '1px', borderBottom: 'none' }}>
                                            <div style={{ background: primaryColor, height: '1px' }}></div>
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : selectedWatchlist?.watchlistItems?.length > 0 ? (
                <>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                        height={'32px'}
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            background: (theme) => theme.palette.background.default,
                            padding: '8px 8px 8px 20px',
                            borderBottom: '1px solid',
                            borderColor: (theme) => theme.palette.divider
                        }}
                    >
                        <Typography fontSize={'14px'}>Symbol</Typography>
                        {shouldAllowAccess(selectedWatchlist, props.editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && (
                            <Stack direction="row" alignItems="center" onClick={handleClick} sx={{ cursor: 'pointer', paddingRight: '5px' }}>
                                <Typography fontSize={'14px'}>Sort by</Typography>
                                {anchorEl ? <ExpandLess /> : <ExpandMore />}
                            </Stack>
                        )}
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ px: 1, py: 1, color: 'text.secondary' }}>
                                Customized order
                            </Typography>
                            <Divider />

                            {SORT_OPTIONS.map((option, index) => {
                                const isActive = sortColumn === option.column && sortDirection === option.direction
                                const isAsc = sortDirection === 'asc'

                                return (
                                    <React.Fragment key={`${option.column}-${option.direction}`}>
                                        <MenuItem
                                            onClick={() => {
                                                if (isActive) {
                                                    setSortDirection(isAsc ? 'desc' : 'asc')
                                                } else {
                                                    setSortColumn(option.column)
                                                    setSortDirection(option.direction as any)
                                                }
                                                setAnchorEl(null)
                                            }}
                                            sx={{ display: 'flex', justifyContent: 'space-between' }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                {option.direction === 'asc' ? <ThinArrowUpwardIcon style={{ fontSize: '14px' }} /> : <ThinArrowDownwardIcon style={{ fontSize: '14px' }} />}
                                                <Typography fontSize="14px" fontWeight={500}>
                                                    {option.label}
                                                </Typography>
                                            </Stack>
                                        </MenuItem>

                                        {[1, 3, 5].includes(index) && <Divider sx={{ marginTop: '0px ! important', marginBottom: '0px !important' }} />}
                                    </React.Fragment>
                                )
                            })}
                        </Menu>
                    </Stack>

                    {
                        <>
                            {sortedWatchlistItems.some((item) => item.item.type === Watchlist_WatchlistItem_Type.SECTION) ? (
                                <WatchListGroupedPanel
                                    data={sortedWatchlistItems}
                                    draggedOverItem={draggedOverItemIndex !== null ? sortedWatchlistItems[draggedOverItemIndex] : null}
                                    selectedWatchlistId={selectedWatchlistId}
                                    handleWatchlistItemAddOrDelete={handleSectionDelete}
                                    subscribeOrderBook={subscribeOrderBook}
                                    handleDragOver={handleDragOver}
                                    handleDragEnd={handleDragEnd}
                                    handleDragStart={handleDragStart}
                                    onItemClick={handleItemClick as any}
                                    selectedItemIds={new Set(selectedItems?.map((i) => i?.id))}
                                    totalItems={flatFocusableItems.length}
                                    focusedIndex={focusedIndex}
                                    setFocusedIndex={setFocusedIndex}
                                    isFocused={focusedIndex as any}
                                    focusTriggeredByKeyboard={focusTriggeredByKeyboard}
                                    editMode={props.editMode}
                                    selectedWatchlist={selectedWatchlist}
                                />
                            ) : (
                                sortedWatchlistItems.map((data, index) => (
                                    <WatchListNormalView
                                        key={data.id}
                                        isDraggedOver={draggedOverItemIndex !== null && sortedWatchlistItems[draggedOverItemIndex]?.id === data.id}
                                        data={data}
                                        watchlistId={selectedWatchlistId}
                                        addOrRemoveWatchlistDataItem={handleSectionDelete}
                                        subscribeSecurity={subscribeOrderBook}
                                        handleDragOver={handleDragOver}
                                        handleDragEnd={handleDragEnd}
                                        handleDragStart={handleDragStart}
                                        index={index}
                                        isFocused={focusedIndex === index}
                                        totalItems={sortedWatchlistItems.length}
                                        setFocusedIndex={setFocusedIndex}
                                        onItemClick={handleItemClick as any}
                                        selectedItemIds={new Set(selectedItems?.map((i) => i?.id))}
                                        focusTriggeredByKeyboard={focusTriggeredByKeyboard}
                                        editMode={props.editMode}
                                    />
                                ))
                            )}
                            {draggedOverItemIndex === sortedWatchlistItems.length ? <ListItem sx={{ height: '1px', background: primaryColor, p: 0, boxSizing: 'border-box' }}></ListItem> : null}
                        </>
                    }
                </>
            ) : (
                <Stack className={classes.emptyWatchlistContainer}>
                    <EmptyWatchlist />
                    <Stack>
                        <Typography variant="body2">{'Your watchlist is waiting for action!'}</Typography>
                        <Typography variant="body2">{"Add a symbol and let's get started"}</Typography>
                    </Stack>
                    {selectedWatchlist && shouldAllowAccess(selectedWatchlist, props.editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && Object.keys(watchlists).length > 0 && (
                        <Button variant="contained" color="inherit" onClick={onAddSymbolButtonClick}>
                            Add symbol
                        </Button>
                    )}
                </Stack>
            )}
        </Stack>
    )
}
